/**
 * Ticket Lifecycle v2 — Lifecycle OptiTime estimate helper (Slice 5B)
 *
 * Pure helper that produces a *separate* vehicle-in-shop time estimate
 * ("Lifecycle OptiTime") from already-computed building blocks:
 *
 *   lifecycleOptiTimeMinutes = techOptiTimeMinutes + advisorDelayImpactMinutes
 *
 * Conceptual distinction (load-bearing):
 *
 *   Tech OptiTime      = technician/bay schedule wall-clock.
 *                        Lives in `tickets.opti_time`. Drives
 *                        Chronomancer, bay blocking, smart assign,
 *                        rollover, timer, parking, invariants.
 *                        **This helper does NOT modify it.**
 *
 *   Lifecycle OptiTime = estimated total *vehicle-in-shop* time across
 *                        the workflow. Includes advisor/front-of-house
 *                        delay where reliably attributable. **Display
 *                        and planning only.** Never written back to
 *                        `tickets.opti_time` / `tickets.opti_finish`.
 *                        Never read by Chronomancer or any scheduling
 *                        hot path.
 *
 * Scope: **analytics math only.** This helper does NOT feed Tech/Bay
 * OptiTime, Chronomancer, scheduling, smart assign, timer, parking,
 * rollover, bay blocking, or any mutation path. It does not replace
 * lifecycle prediction (slice 5A.6, deferred) — it is the simplest
 * first vehicle-in-shop estimate built from inputs that already exist
 * today.
 *
 * Why this lives behind a pure helper and not in the UI:
 *   - It will need unit tests independent of any React tree.
 *   - It will eventually be consumed by more than one surface
 *     (TicketLifecycleTimeCard, promise-time / due-out, calendar load,
 *     planning previews). Centralising the formula avoids drift.
 *   - It must be auditable in isolation: same inputs ⇒ same outputs.
 *
 * Method semantics:
 *
 *   - `'tech_plus_advisor_delay'` — both `techOptiTimeMinutes` and
 *     `advisorDelayImpactMinutes` are finite numbers (negative values
 *     are clamped to 0 before adding). Lifecycle estimate is the sum.
 *
 *   - `'tech_only'` — tech is a finite number, but
 *     `advisorDelayImpactMinutes` is `null`/`undefined`/non-finite.
 *     Lifecycle falls back to clamped tech and the `notes` array
 *     surfaces the gap. Rationale: tech alone is still a *useful lower
 *     bound* on vehicle-in-shop time. Returning `null` here would deny
 *     callers any estimate at all; `'tech_only'` keeps the helper
 *     defensible without overstating delay impact.
 *
 *   - `'insufficient_data'` — tech is `null`/`undefined`/non-finite.
 *     Without a tech wall-clock anchor the lifecycle estimate has no
 *     meaningful floor; returning `null` is the only safe choice.
 *
 * Forward-compatibility (NOT used in slice 5B):
 *
 *   `knownHoldMinutes` and `knownPartsWaitMinutes` are accepted in the
 *   input shape so callers can be wired up early, but they are **not
 *   folded into `lifecycleOptiTimeMinutes`** this slice. Reasons:
 *
 *     - `tickets.pause_reason` is free-text and not yet categorised
 *       (customer hold / parts hold / approval hold).
 *     - Parts-side advisor actions (`parts_ordered`, `parts_received`,
 *       `parts_returned`) are declared in the enum but their wiring
 *       remains flag-gated (slice 4F flag default OFF).
 *
 *   Until those signals are reliable, this helper documents that they
 *   were supplied (via `notes`) and skips them. A future slice will
 *   either (a) introduce explicit `'tech_plus_advisor_delay_plus_holds'`
 *   method variants, or (b) expand the input contract — without
 *   rewriting the call sites that already pass these fields.
 *
 * Purity contract:
 *   - Same inputs always produce the same output.
 *   - No `Date.now()`, no `new Date()`, no I/O.
 *   - Does not mutate the input object.
 *   - Negative inputs clamp to 0; the output is never negative.
 *
 * Boundary: this module is analytics-only. It must not be imported into
 * Chronomancer / scheduling / timer / parking / rollover / bay /
 * invariants hot paths. The drift detector boundary guard pins this.
 */

import type { AdvisorBucketConfidence } from './advisorBucketMetrics';
import type { TicketJobMixBucket } from './jobMixClassifier';

export type LifecycleOptiTimeMethod =
  | 'tech_plus_advisor_delay'
  | 'tech_plus_predicted_advisor_delay'
  | 'tech_only'
  | 'insufficient_data';

/**
 * Slice 5D-4: optional historical-prediction input. Supplied by callers
 * (typically `TicketLifecycleTimeCard`) when observed advisor delay is
 * unavailable but the current advisor has a usable bucket-level history.
 *
 *   minutes      — `averageAdvisorDelayImpactMinutes` for the matching
 *                  bucket. `null` means "the bucket has no usable mean"
 *                  (e.g. every diag_plus_work sample missing remaining,
 *                  or work_with_dependency).
 *   confidence   — categorical confidence from
 *                  `classifyAdvisorBucketConfidence`. `'insufficient'`
 *                  is filtered out by the helper — predictions only fire
 *                  at `'low' | 'medium' | 'high'`.
 *   bucket       — the TicketJobMixBucket the prediction was drawn from.
 *                  Echoed in the result so display can name it.
 *   bucketLabel  — optional pretty label (e.g. "Inspection/Diag Only"),
 *                  passed through verbatim. Display falls back to the
 *                  raw bucket key when absent.
 *   sampleSize   — bucket-level sample size for display ("based on
 *                  N tickets").
 *
 * The shape is conservative — null `minutes` or `'insufficient'`
 * confidence cause the helper to *not* use the prediction. Callers do
 * not need to pre-filter.
 */
export interface LifecycleOptiTimePredictedDelay {
  minutes: number | null;
  confidence: AdvisorBucketConfidence;
  bucket: TicketJobMixBucket;
  bucketLabel?: string;
  sampleSize: number;
}

export interface LifecycleOptiTimeEstimateInput {
  /**
   * Technician/bay wall-clock minutes from `tickets.opti_time` (or the
   * equivalent `calculateOptiTime` value already used by
   * `TicketLifecycleTimeCard`). `null`/`undefined`/non-finite ⇒ the
   * helper refuses (`'insufficient_data'`).
   */
  techOptiTimeMinutes: number | null | undefined;

  /**
   * Output of `calculateAdvisorDelayImpact` (slice 5A.6-lite) — the
   * per-bucket estimate of how much advisor/FOH time likely delayed
   * the ticket lifecycle. `null` is a valid value meaning "unknown /
   * not yet computable" (e.g. `diag_plus_work` without
   * `remainingApprovedWork`).
   */
  advisorDelayImpactMinutes: number | null | undefined;

  /**
   * Slice 5D-4: optional per-advisor × per-bucket historical
   * prediction. Used **only** when `advisorDelayImpactMinutes` is
   * null/undefined/non-finite AND this object exists AND its
   * `confidence` is `'low' | 'medium' | 'high'` AND its `minutes` is
   * a finite number. Otherwise ignored.
   */
  predictedAdvisorDelay?: LifecycleOptiTimePredictedDelay | null;

  /**
   * **Reserved for a future slice.** Not yet folded into the estimate
   * because there is no reliable runtime hold/dependency signal today
   * (`tickets.pause_reason` is free-text). Pass-through only — surfaced
   * in `notes` so callers can see they were ignored intentionally.
   */
  knownHoldMinutes?: number | null;

  /**
   * **Reserved for a future slice.** Not yet folded into the estimate
   * because parts-side advisor action events are flag-gated and
   * dormant by default. Pass-through only — surfaced in `notes`.
   */
  knownPartsWaitMinutes?: number | null;
}

export interface LifecycleOptiTimeEstimateResult {
  /**
   * The lifecycle estimate in minutes; `null` when the helper refused
   * to commit (`method === 'insufficient_data'`). Never negative.
   */
  lifecycleOptiTimeMinutes: number | null;
  /**
   * Echo of the clamped technician minutes used in the calculation, or
   * `null` when the input was missing/non-finite. Helpful for callers
   * who want to render "Tech: X · Advisor delay: Y · Lifecycle: X+Y".
   */
  techOptiTimeMinutes: number | null;
  /**
   * Echo of the clamped advisor delay minutes used in the calculation,
   * or `null` when the input was missing/non-finite.
   */
  advisorDelayImpactMinutes: number | null;
  /**
   * Slice 5D-4: echo of the predicted advisor delay actually consumed,
   * with `minutes` clamped non-negative. `null` when no prediction was
   * provided OR the prediction was filtered out (insufficient
   * confidence / null minutes). Lets display surfaces render
   * "Predicted: 18m · {bucketLabel} ({confidence})" without re-reading
   * the input.
   */
  predictedAdvisorDelay: LifecycleOptiTimePredictedDelay | null;
  /** Which formula branch produced the result. */
  method: LifecycleOptiTimeMethod;
  /**
   * Human-readable explanations for the chosen method. Always at least
   * one entry. Useful for UI tooltips and for proving in tests that
   * the helper documented its refusals.
   */
  notes: string[];
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clampNonNegative(value: number): number {
  return value < 0 ? 0 : value;
}

/**
 * Compute a vehicle-in-shop / Lifecycle OptiTime estimate. Pure: same
 * inputs always produce the same output.
 *
 * @see calculateAdvisorDelayImpact (slice 5A.6-lite) for how
 *      `advisorDelayImpactMinutes` is produced from the job-mix bucket.
 * @see deriveTimeBuckets (slice 5A.1) — `executionMinutes` is the
 *      canonical source for `techOptiTimeMinutes`.
 * @see docs/lifecycle/OPTITIME_LIFECYCLE_BUCKETS_PLAN.md §Slice 5B.
 */
/**
 * Slice 5D-4: decide whether a supplied prediction is usable. Returns
 * the clamped minutes when usable, or `null` when filtered out.
 * Exported for unit testing — the filter rules are pinned by the drift
 * detector to keep the prediction surface honest.
 *
 * Rules:
 *   - `null`/`undefined` prediction object → unusable.
 *   - `null`/`undefined`/non-finite `minutes` → unusable. Treats null
 *     as "unknown", not "0", per slice 5D-3 contract.
 *   - `confidence === 'insufficient'` → unusable. Predictions only fire
 *     at `'low' | 'medium' | 'high'`.
 *   - Otherwise → usable, return clamped non-negative minutes.
 */
export function selectPredictedAdvisorDelayMinutes(
  prediction: LifecycleOptiTimePredictedDelay | null | undefined,
): number | null {
  if (!prediction) return null;
  if (!isFiniteNumber(prediction.minutes)) return null;
  if (prediction.confidence === 'insufficient') return null;
  return clampNonNegative(prediction.minutes);
}

export function calculateLifecycleOptiTimeEstimate(
  input: LifecycleOptiTimeEstimateInput,
): LifecycleOptiTimeEstimateResult {
  const notes: string[] = [];

  // Optional hold/parts fields are documented but not used in slice 5B.
  // We surface their presence so a future change of behaviour is loud.
  if (
    input.knownHoldMinutes !== undefined &&
    input.knownHoldMinutes !== null
  ) {
    notes.push(
      'knownHoldMinutes supplied but not yet folded into the lifecycle estimate (reserved for a future slice).',
    );
  }
  if (
    input.knownPartsWaitMinutes !== undefined &&
    input.knownPartsWaitMinutes !== null
  ) {
    notes.push(
      'knownPartsWaitMinutes supplied but not yet folded into the lifecycle estimate (reserved for a future slice).',
    );
  }

  // Slice 5D-4: filter the prediction once so every branch references
  // the same usable-or-null value. The clamped minutes are stashed for
  // the predicted-branch arithmetic; the original prediction object is
  // echoed in the result whenever a usable prediction was chosen, so
  // the display layer can name the bucket + confidence + sample size.
  const usablePredictedMinutes = selectPredictedAdvisorDelayMinutes(
    input.predictedAdvisorDelay,
  );

  if (!isFiniteNumber(input.techOptiTimeMinutes)) {
    return {
      lifecycleOptiTimeMinutes: null,
      techOptiTimeMinutes: null,
      advisorDelayImpactMinutes: isFiniteNumber(input.advisorDelayImpactMinutes)
        ? clampNonNegative(input.advisorDelayImpactMinutes)
        : null,
      predictedAdvisorDelay: null,
      method: 'insufficient_data',
      notes: [
        'Tech OptiTime is missing or not finite; lifecycle estimate cannot be anchored.',
        ...notes,
      ],
    };
  }

  const clampedTech = clampNonNegative(input.techOptiTimeMinutes);

  // Branch 1: observed advisor delay (slice 5B; always preferred).
  if (isFiniteNumber(input.advisorDelayImpactMinutes)) {
    const clampedAdvisor = clampNonNegative(input.advisorDelayImpactMinutes);
    return {
      lifecycleOptiTimeMinutes: clampedTech + clampedAdvisor,
      techOptiTimeMinutes: clampedTech,
      advisorDelayImpactMinutes: clampedAdvisor,
      predictedAdvisorDelay: null,
      method: 'tech_plus_advisor_delay',
      notes: [
        'Lifecycle estimate = Tech OptiTime + Advisor Delay Impact.',
        ...notes,
      ],
    };
  }

  // Branch 2: predicted advisor delay (slice 5D-4). Only when observed
  // is unavailable AND a usable prediction was supplied. Confidence
  // filtering happens inside `selectPredictedAdvisorDelayMinutes`.
  if (usablePredictedMinutes !== null && input.predictedAdvisorDelay) {
    return {
      lifecycleOptiTimeMinutes: clampedTech + usablePredictedMinutes,
      techOptiTimeMinutes: clampedTech,
      advisorDelayImpactMinutes: null,
      predictedAdvisorDelay: {
        ...input.predictedAdvisorDelay,
        minutes: usablePredictedMinutes,
      },
      method: 'tech_plus_predicted_advisor_delay',
      notes: [
        'Lifecycle estimate = Tech OptiTime + Predicted Advisor Delay (from this advisor\'s bucket average).',
        ...notes,
      ],
    };
  }

  // Branch 3: tech-only lower bound.
  return {
    lifecycleOptiTimeMinutes: clampedTech,
    techOptiTimeMinutes: clampedTech,
    advisorDelayImpactMinutes: null,
    predictedAdvisorDelay: null,
    method: 'tech_only',
    notes: [
      'Advisor delay impact unavailable; lifecycle estimate uses Tech OptiTime as a lower bound only.',
      ...notes,
    ],
  };
}
